import { useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { isNetworkError, handleNetworkError } from '../../utils/api/networkErrors';

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  const checkAdminStatus = async (userId: string): Promise<boolean> => {
    try {
      // Try up to 3 times with exponential backoff
      for (let i = 0; i < 3; i++) {
        try {
          const { data: profile, error } = await supabase
            .from('user_profiles')
            .select('is_admin')
            .eq('id', userId)
            .maybeSingle();

          if (error) {
            if (error.code === 'PGRST116' || isNetworkError(error)) {
              // Wait longer between each retry
              await new Promise(resolve => setTimeout(resolve, 2000 * Math.pow(2, i)));
              continue;
            }
            throw error;
          }

          return !!profile?.is_admin;
        } catch (retryError) {
          if (i === 2) throw retryError; // Re-throw on final attempt
          continue;
        }
      }

      return false;
    } catch (error) {
      if (isNetworkError(error)) {
        handleNetworkError(error, () => checkAdminStatus(userId));
      } else {
        console.error('Error checking admin status:', error);
      }
      return false;
    }
  };
  
  return {
    user: context.user,
    isAdmin: context.isAdmin,
    isAuthenticated: context.isAuthenticated,
    isLoading: context.isLoading,
    signOut: context.signOut
  };
}